import { RefObject, useMemo, useRef, useState } from 'react';

import { throttle } from 'es-toolkit/compat';
import { GeoJSONFeature, MapMouseEvent } from 'mapbox-gl';
import { MapRef } from 'react-map-gl/mapbox';

import { THROTTLE_INTERVAL_MS } from '@/app/constants';

interface MapTooltipProps {
  mapSourceLayer: string | null;
  mapSourcePrefix: string;
  mapsRefs: RefObject<(MapRef | null)[]>;
}

const useMapTooltip = ({
  mapSourceLayer,
  mapSourcePrefix,
  mapsRefs,
}: MapTooltipProps) => {
  const lastHoveredId = useRef<string | number>('');
  const [hoverInfo, setHoverInfo] = useState<{
    feature: GeoJSONFeature;
    x: number;
    y: number;
  } | null>(null);

  const throttledOnHover = useMemo(
    () =>
      throttle(
        (...args: unknown[]) => {
          if (!mapSourceLayer) {
            return;
          }

          const event = args[0] as MapMouseEvent;
          const {
            features,
            point: { x, y },
          } = event;
          const hoveredFeature = features
            ? (features[0] as GeoJSONFeature | null)
            : null;
          const newId: number | string =
            (hoveredFeature?.properties?.id as number | string | undefined) ??
            '';

          if (newId !== lastHoveredId.current) {
            // Remove old hover state
            if (lastHoveredId.current) {
              mapsRefs.current.forEach((mapRef, index) => {
                mapRef?.getMap().removeFeatureState(
                  {
                    source: `${mapSourcePrefix}${index.toString()}`,
                    sourceLayer: mapSourceLayer,
                    id: lastHoveredId.current,
                  },
                  'hovered'
                );
              });
            }

            // Set new hover state
            if (newId) {
              mapsRefs.current.forEach((mapRef, index) => {
                mapRef?.getMap().setFeatureState(
                  {
                    source: `${mapSourcePrefix}${index.toString()}`,
                    sourceLayer: mapSourceLayer,
                    id: newId,
                  },
                  { hovered: true }
                );
              });
            }

            lastHoveredId.current = newId;
          }

          if (hoveredFeature) {
            setHoverInfo({ feature: hoveredFeature, x, y: y + 4 });
          } else {
            setHoverInfo(null);
          }
        },
        THROTTLE_INTERVAL_MS,
        { leading: true, trailing: false }
      ),
    [mapSourceLayer, mapSourcePrefix]
  );

  const removeHoverInfo = () => {
    if (!mapSourceLayer) {
      return;
    }

    if (lastHoveredId.current) {
      mapsRefs.current.forEach((mapRef, index) => {
        mapRef?.getMap().removeFeatureState(
          {
            source: `${mapSourcePrefix}${index.toString()}`,
            sourceLayer: mapSourceLayer,
            id: lastHoveredId.current,
          },
          'hovered'
        );
      });
      lastHoveredId.current = '';
    }
    setHoverInfo(null);
  };

  return { hoverInfo, throttledOnHover, removeHoverInfo };
};

export default useMapTooltip;
